import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';

import Select from '../../../components/Select';
import Modal from '../../../components/Modal';
import { PRODUCT, REQUEST } from '../../../utils/const/formTypes';
import { getLabelOptions } from '../../../utils/date';
import useStyles from './styles';

const TaskSettings = ({
  open,
  setOpen,
  georeference,
  setGeoreference,
  aiAnalysisRequired,
  setAiAnalysisRequired,
  revisionPolicy,
  setRevisionPolicy,
  surveyLabel,
  setSurveyLabel,
  isSurveyTeleaudit,
  setIsSurveyTeleaudit,
  verticalForm,
  handleVerticalForm,
  formType,
  maxDistance,
  setMaxDistance,
  needReview,
  setNeedReview,
  executionSurveyId,
  setExecutionSurveyId,
  reviewSurveyId,
  setReviewSurveyId,
  executionSurveyOptions,
  reviewSurveyOptions,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const revisionOptions = [
    {
      name: t('task.taskEdition.revisionPolicy.placeholder'),
      value: '',
    },
    {
      name: t('task.taskEdition.revisionPolicy.yes'),
      value: 'yes',
    },
    {
      name: t('task.taskEdition.revisionPolicy.no'),
      value: 'no',
    },
    {
      name: t('task.taskEdition.revisionPolicy.random'),
      value: 'random',
    },
  ];

  const labelOptions = getLabelOptions(t);

  const isTeleauditOptions = [
    {
      name: t('task.taskEdition.revisionPolicy.placeholder'),
      value: '',
    },
    {
      name: t('task.taskEdition.revisionPolicy.yes'),
      value: 'yes',
    },
    {
      name: t('task.taskEdition.revisionPolicy.no'),
      value: 'no',
    },
  ];

  const confirmationOptions = [
    {
      name: t('task.taskEdition.needConfirmation.placeholder'),
      value: '',
    },
    {
      name: t('task.taskEdition.needConfirmation.applicantReview'),
      value: 'applicationReview',
    },
    {
      name: t('task.taskEdition.needConfirmation.otherReview'),
      value: 'otherReview',
    },
    {
      name: t('task.taskEdition.needConfirmation.no'),
      value: 'no',
    },
  ];

  const handleOpen = () => {
    setOpen(!open);
  };

  return (
    <Modal
      title={t('task.taskEdition.settings')}
      defaultOpen={open}
      setCurrentOpen={handleOpen}
    >
      <div className={styles.formWrapper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={(
                <Switch
                  checked={georeference}
                  onChange={() => setGeoreference(!georeference)}
                  color="primary"
                />
              )}
              label={t('task.taskEdition.georeference')}
            />
            {georeference && (
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                <Grid item className={styles.distanceField}>
                  <TextField
                    type="number"
                    value={maxDistance}
                    required={georeference}
                    inputProps={{ min: 0 }}
                    onChange={(event) => setMaxDistance(event.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item>
                  <Box display="flex" alignItems="center" className={styles.caption}>
                    <Box className={styles.icon}>
                      <PriorityHighIcon htmlColor="#2196F3" />
                    </Box>
                    <Typography variant="caption" className={styles.captionText}>
                      {t('task.taskEdition.maxDistance')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={(
                <Switch
                  checked={aiAnalysisRequired}
                  onChange={() => setAiAnalysisRequired(!aiAnalysisRequired)}
                  color="primary"
                />
              )}
              label={t('task.taskEdition.aiAnalysisSwitch')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>{t('task.taskEdition.label')}</Typography>
            <Select
              name="survey-label"
              items={labelOptions}
              value={surveyLabel ? surveyLabel.value : ''}
              onChange={(event) => setSurveyLabel(event.target)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Tooltip title={t('task.taskEdition.isSurveyTeleauditTooltip')}>
              <Typography>{t('task.taskEdition.isSurveyTeleaudit')}</Typography>
            </Tooltip>
            <Select
              name="is-survey-teleaudit"
              items={isTeleauditOptions}
              value={isSurveyTeleaudit ? isSurveyTeleaudit.value : ''}
              onChange={(event) => setIsSurveyTeleaudit(event.target)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>{t('task.taskEdition.revisionPolicyQuestion')}</Typography>
            <Select
              name="requires-revision"
              items={revisionOptions}
              value={revisionPolicy ? revisionPolicy.value : ''}
              onChange={(event) => setRevisionPolicy(event.target)}
              fullWidth
            />
          </Grid>
          {formType.name === REQUEST && (
            <Grid item xs={12} sm={6}>
              <Typography>{t('task.taskEdition.needConfirmationQuestion')}</Typography>
              <Select
                name="need-review"
                items={confirmationOptions}
                value={needReview ? needReview.value : ''}
                onChange={(event) => setNeedReview(event.target)}
                fullWidth
              />
            </Grid>
          )}

          {formType.name === REQUEST && (
            <Grid item xs={12} sm={6}>
              <Typography>{t('task.taskEdition.executionSurvey')}</Typography>
              <Select
                name="execution-survey"
                items={executionSurveyOptions || []}
                value={executionSurveyId ? executionSurveyId.value : ''}
                onChange={(event) => setExecutionSurveyId(event.target)}
                fullWidth
              />
            </Grid>
          )}
          {formType.name === REQUEST && (
            <Grid item xs={12} sm={6}>
              <Typography>{t('task.taskEdition.reviewSurvey')}</Typography>
              <Select
                name="review-survey"
                items={reviewSurveyOptions || []}
                value={reviewSurveyId ? reviewSurveyId.value : ''}
                onChange={(event) => setReviewSurveyId(event.target)}
                fullWidth
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              className={`${styles.switch} ${styles.switchVerticalOption}`}
              control={(
                <Switch
                  checked={formType.name === PRODUCT || verticalForm}
                  onChange={handleVerticalForm}
                  color="primary"
                />
              )}
              label={t('task.taskEdition.verticalForm')}
              disabled={formType.name === PRODUCT}
            />
            <Box display="flex" alignItems="center" className={styles.caption}>
              <Box className={styles.icon}>
                <PriorityHighIcon htmlColor="#2196F3" />
              </Box>
              <Typography variant="caption" className={styles.captionText}>
                {t('task.taskEdition.verticalFormCaption')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};

const optionShape = PropTypes.shape({
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
});

TaskSettings.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  georeference: PropTypes.bool,
  setGeoreference: PropTypes.func,
  aiAnalysisRequired: PropTypes.bool,
  setAiAnalysisRequired: PropTypes.func,
  revisionPolicy: optionShape,
  setRevisionPolicy: PropTypes.func,
  surveyLabel: optionShape,
  setSurveyLabel: PropTypes.func,
  isSurveyTeleaudit: optionShape,
  setIsSurveyTeleaudit: PropTypes.func,
  verticalForm: PropTypes.bool,
  handleVerticalForm: PropTypes.func,
  formType: optionShape,
  maxDistance: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  setMaxDistance: PropTypes.func,
  needReview: optionShape,
  setNeedReview: PropTypes.func,
  executionSurveyId: optionShape,
  setExecutionSurveyId: PropTypes.func,
  reviewSurveyId: optionShape,
  setReviewSurveyId: PropTypes.func,
  executionSurveyOptions: PropTypes.arrayOf(optionShape),
  reviewSurveyOptions: PropTypes.arrayOf(optionShape),
};

export default TaskSettings;
